import Label from "@/components/forms/Label"
import NumberInput from "@/components/forms/NumberInput"
import RadioGroup from "@/components/forms/RadioGroup"
import { useState, useEffect } from "react"

/**
 * LoudnessTab Component
 *
 * Manages loudness settings with:
 * - Enable/Disable toggle
 * - Reference Level adjustment
 * - Loudness Lowshelf filter settings
 * - Loudness Highshelf filter settings
 */
type LoudnessTabProps = {
  eqSettings: any
  mode: string
  onEqChange: (changes: any[]) => void
  defaultSettings: any
}

const LoudnessTab = ({ eqSettings, onEqChange, mode, defaultSettings }: LoudnessTabProps) => {
  const [loudnessEnabled, setLoudnessEnabled] = useState<string>("OFF")
  const [referenceLevel, setReferenceLevel] = useState<number>(-20)
  const [lowshelfGain, setLowshelfGain] = useState<number>(10)
  const [lowshelfFreq, setLowshelfFreq] = useState<number>(70)
  const [highshelfGain, setHighshelfGain] = useState<number>(10)
  const [highshelfFreq, setHighshelfFreq] = useState<number>(3500)

  /**
   * On settings change
   *
   * When parent settings change, update loudness settings
   */
  useEffect(() => {
    if (eqSettings) {
      // Get default settings based on current mode
      const defaultLoudnessSettings =
        defaultSettings.Configuration.DSP[mode]?.Loudness ||
        defaultSettings.Configuration.DSP.Headphone.Loudness
      const defaultLowshelfSettings =
        defaultSettings.Configuration.DSP[mode]?.LoudnessLowshelf ||
        defaultSettings.Configuration.DSP.Headphone.LoudnessLowshelf
      const defaultHighshelfSettings =
        defaultSettings.Configuration.DSP[mode]?.LoudnessHighshelf ||
        defaultSettings.Configuration.DSP.Headphone.LoudnessHighshelf

      // Handle missing Loudness settings in older presets
      const loudnessSettings = eqSettings["Loudness"] || defaultLoudnessSettings
      const lowshelfSettings = eqSettings["LoudnessLowshelf"] || defaultLowshelfSettings
      const highshelfSettings = eqSettings["LoudnessHighshelf"] || defaultHighshelfSettings

      setLoudnessEnabled(loudnessSettings["Enabled"]["Current"])
      setReferenceLevel(parseFloat(loudnessSettings["ReferenceLevel"]["Current"]))
      setLowshelfGain(parseFloat(lowshelfSettings["Gain"]["Current"]))
      setLowshelfFreq(parseFloat(lowshelfSettings["Frequency"]["Current"]))
      setHighshelfGain(parseFloat(highshelfSettings["Gain"]["Current"]))
      setHighshelfFreq(parseFloat(highshelfSettings["Frequency"]["Current"]))
    }
  }, [eqSettings, mode])

  /**
   * Update
   *
   * @param {*} domain
   * @param {*} value
   * @returns
   */
  const update = async (domain, value) => {
    const success = await onEqChange([["Loudness", domain, value]])
    return success
  }

  /**
   * Update filter settings
   *
   * @param {*} filterName
   * @param {*} domain
   * @param {*} value
   * @returns
   */
  const updateFilter = async (filterName, domain, value) => {
    const success = await onEqChange([[filterName, domain, value]])
    return success
  }

  // Get settings with fallbacks for older presets
  const defaultLoudnessSettings =
    defaultSettings.Configuration.DSP[mode]?.Loudness ||
    defaultSettings.Configuration.DSP.Headphone.Loudness
  const defaultLowshelfSettings =
    defaultSettings.Configuration.DSP[mode]?.LoudnessLowshelf ||
    defaultSettings.Configuration.DSP.Headphone.LoudnessLowshelf
  const defaultHighshelfSettings =
    defaultSettings.Configuration.DSP[mode]?.LoudnessHighshelf ||
    defaultSettings.Configuration.DSP.Headphone.LoudnessHighshelf

  const loudnessSettings = eqSettings?.["Loudness"] || defaultLoudnessSettings
  const lowshelfSettings = eqSettings?.["LoudnessLowshelf"] || defaultLowshelfSettings
  const highshelfSettings = eqSettings?.["LoudnessHighshelf"] || defaultHighshelfSettings

  return (
    <div className="space-y-6">
      <div className="grid gap-x-5 gap-y-1" style={{ gridTemplateColumns: "auto 1fr" }}>
        <div className="flex items-center gap-x-8">
          <div className="flex items-center gap-x-3">
            <Label
              text="Loudness"
              info={
                <div className="max-w-md space-y-1">
                  <p>Enables/disables loudness compensation based on volume level.</p>
                  <p>
                    Note: Loudness settings are not saved in Cloud presets, but will be saved with
                    any Profile stored directly on your Element IV.
                  </p>
                </div>
              }
            />
            <RadioGroup
              selected={loudnessEnabled}
              items={loudnessSettings["Enabled"]["Elements"].map((settingsValue) => {
                return { label: settingsValue, value: settingsValue }
              })}
              onValueChange={(value) => update("Enabled", value)}
              isSmall
              className=""
            />
          </div>

          <div className="h-6 w-px bg-tundora" />

          <div className="flex items-center gap-x-3">
            <Label
              text="Reference Level"
              info="Volume level at which no loudness compensation is applied (-127 to +14 dB)"
            />
            <NumberInput
              value={referenceLevel}
              min={loudnessSettings["ReferenceLevel"]["Min"]}
              max={loudnessSettings["ReferenceLevel"]["Max"]}
              tabIndex={1}
              onDebouncedChange={(value) => update("ReferenceLevel", value)}
              disabled={loudnessEnabled === "OFF"}
            />
          </div>
        </div>
      </div>

      <div className="ml-0">
        <div
          className="grid gap-x-2 gap-y-1"
          style={{ gridTemplateColumns: "auto 100px 100px", width: "fit-content" }}
        >
          <div />
          <Label text="Lowshelf" />
          <Label text="Highshelf" />

          <Label text="Max Boost (dB)" />
          <NumberInput
            value={lowshelfGain}
            min={lowshelfSettings["Gain"]["Min"]}
            max={lowshelfSettings["Gain"]["Max"]}
            tabIndex={2}
            onDebouncedChange={(value) => updateFilter("LoudnessLowshelf", "Gain", value)}
            disabled={loudnessEnabled === "OFF"}
          />
          <NumberInput
            value={highshelfGain}
            min={highshelfSettings["Gain"]["Min"]}
            max={highshelfSettings["Gain"]["Max"]}
            tabIndex={3}
            onDebouncedChange={(value) => updateFilter("LoudnessHighshelf", "Gain", value)}
            disabled={loudnessEnabled === "OFF"}
          />

          <Label text="Frequency (Hz)" />
          <NumberInput
            value={lowshelfFreq}
            min={lowshelfSettings["Frequency"]["Min"]}
            max={lowshelfSettings["Frequency"]["Max"]}
            tabIndex={4}
            onDebouncedChange={(value) => updateFilter("LoudnessLowshelf", "Frequency", value)}
            disabled={loudnessEnabled === "OFF"}
          />
          <NumberInput
            value={highshelfFreq}
            min={highshelfSettings["Frequency"]["Min"]}
            max={highshelfSettings["Frequency"]["Max"]}
            tabIndex={5}
            onDebouncedChange={(value) => updateFilter("LoudnessHighshelf", "Frequency", value)}
            disabled={loudnessEnabled === "OFF"}
          />
        </div>
      </div>
    </div>
  )
}

export default LoudnessTab
